import React, { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ArrowRight, Info } from 'lucide-react';
import { PORTAL_USER, useSession } from '@/state/session';
import { KEYS } from '@/state/storage';
import { DEFAULT_SCENARIO_ID } from '@/fixtures/scenarios';
import { AboutDialog } from '@/components/app/AboutDialog';
import { IMAGES } from '@/lib/images';
import { Picture } from '@/components/site/Picture';
import { BrandLogo } from '@/components/brand/BrandLogo';

/*
  Demo-inngang til produksjonsportalen. Ærlig og selvinstruerende: det finnes ingen konto, passord eller tilgangsstyring
  i forhåndsvisningen – «Åpne demo» starter en lokal økt med syntetiske eksempeldata. Dette er ikke en sikkerhetsgrense,
  og den later ikke som det (ingen tomt passordfelt). Før reelle data tas i bruk må en backend håndheve pålogging og roller.
*/

function BrandPhoto({ className, imgClassName }) {
  return (
    <div className={className} data-testid="login-left-image-panel">
      <Picture variant={IMAGES.login} sizes="(min-width: 1024px) 52vw, 100vw" priority alt="Hjullaster i steinbruddet til Hardanger Rock" className={imgClassName} style={{ objectPosition: '50% 48%' }} />
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(23,26,43,0.42)_0%,rgba(23,26,43,0.06)_38%,rgba(23,26,43,0)_60%,rgba(23,26,43,0.28)_100%)]" aria-hidden="true" />
      <Link to="/" className="absolute left-6 top-6 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white sm:left-8 sm:top-8 lg:left-10 lg:top-10" aria-label="Hardanger Rock – til nettsiden">
        <BrandLogo variant="whiteOrange" className="h-8 w-auto lg:h-9" testId="login-brand-logo" />
      </Link>
      <div className="absolute bottom-6 left-6 text-[13px] font-medium tracking-wide text-white/80 sm:bottom-8 sm:left-8 lg:bottom-10 lg:left-10" data-testid="login-location">
        Dimmelsvik · Hardanger
      </div>
    </div>
  );
}

export default function LoginPage() {
  const { session, openDemo } = useSession();
  const [aboutOpen, setAboutOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  if (session) return <Navigate to="/oversikt" replace />;

  const submit = (e) => {
    e.preventDefault();
    openDemo();
    const from = location.state?.from;
    const last = window.localStorage.getItem(KEYS.lastView);
    const target = from && from !== '/login' ? from : last && !last.startsWith('/login') ? last : `/oversikt?scenario=${DEFAULT_SCENARIO_ID}`;
    navigate(target, { replace: true });
  };

  return (
    <div className="min-h-screen bg-white text-[var(--hr-text)] lg:grid lg:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)]">
      {/* Ett bildepanel for alle bredder (ikke to skjulte kopier): lavt bånd på mobil, full høyde på desktop. */}
      <BrandPhoto className="relative h-[200px] w-full overflow-hidden sm:h-[260px] lg:h-auto lg:min-h-screen" imgClassName="absolute inset-0 h-full w-full object-cover" />

      <main className="relative flex items-start justify-center px-6 py-10 sm:px-10 lg:min-h-screen lg:items-center lg:py-16" id="main">
        <Link to="/" className="absolute left-6 top-5 hidden items-center gap-1.5 text-[14px] font-medium text-[var(--hr-muted)] transition-colors duration-150 hover:text-[var(--hr-heading)] lg:inline-flex" data-testid="login-back-to-site">
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> Til nettsiden
        </Link>
        <form onSubmit={submit} className="w-full max-w-[380px]" data-testid="login-form" aria-labelledby="login-title">
          <p className="text-[13px] font-semibold uppercase tracking-[0.08em] text-[var(--hr-muted-2)]" data-testid="login-kicker">
            Produksjonsportal · forhåndsvisning
          </p>
          <h1 id="login-title" className="mt-2 text-[26px] font-semibold tracking-[-0.02em] text-[var(--hr-heading)]">
            Åpne demo
          </h1>
          <p className="mt-2 text-[14px] leading-6 text-[var(--hr-muted)]" data-testid="login-lead">
            Ingen innlogging eller passord i denne forhåndsvisningen – du åpner portalen direkte og ser den som brukeren under.
          </p>

          <div className="mt-6 flex items-center gap-3 rounded-[12px] border border-[var(--hr-hairline)] bg-[var(--hr-surface-2)] px-3.5 py-3" data-testid="login-user-card">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--hr-navy)] text-[14px] font-semibold text-white" aria-hidden="true">
              {PORTAL_USER.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}
            </span>
            <span className="min-w-0">
              <span className="block truncate text-[14px] font-semibold text-[var(--hr-heading)]" data-testid="login-user-name">{PORTAL_USER.name}</span>
              <span className="block truncate text-[13px] text-[var(--hr-muted)]" data-testid="login-user-title">{PORTAL_USER.title} · Hardanger Rock · demobruker</span>
            </span>
          </div>

          <Button type="submit" className="mt-6 h-12 w-full rounded-[10px] bg-[var(--hr-navy)] text-[16px] font-semibold text-white shadow-none transition-colors duration-150 hover:bg-[var(--hr-blue)] lg:h-11" data-testid="login-submit-button">
            Åpne demo <ArrowRight className="ml-1.5 h-4 w-4" aria-hidden="true" />
          </Button>

          <div className="mt-5 flex gap-2.5 rounded-[12px] border border-[var(--hr-hairline)] px-3.5 py-3 text-[13px] leading-5 text-[var(--hr-muted)]" role="note" data-testid="login-synthetic-note">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-[var(--hr-accent-text)]" aria-hidden="true" />
            <p>
              Alt innhold er <span className="font-medium text-[var(--hr-text)]">syntetiske eksempeldata</span>: fiktive medarbeidere, kunder, prosjekter og registreringer. Økten lagres bare i denne nettleseren. Ikke en sikkerhetsgrense – reell pålogging og roller må på plass før virkelige data brukes.
            </p>
          </div>

          <p className="mt-8 text-[13px] leading-5 text-[var(--hr-muted-2)]" data-testid="login-prototype-note">
            Spørsmål om portalen?{' '}
            <a href="mailto:post@hardangerrock.no" className="hr-link" data-testid="login-help-link">
              post@hardangerrock.no
            </a>
            {' · '}
            <button type="button" className="hr-link" onClick={() => setAboutOpen(true)} data-testid="login-about-button">
              Om portalen
            </button>
          </p>
        </form>
      </main>
      <AboutDialog open={aboutOpen} onOpenChange={setAboutOpen} />
    </div>
  );
}
